import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { settingsApi, techniciansApi, apiTokensApi, aiApi, backupApi, serverApi, connectionsApi } from "../services/api";
import { useNotification } from "../context/NotificationContext";


export function BlocklistSection({ title, description, type, placeholder, entries, onAdd, onRemove, isAdding, isRemoving = false, loadFailed = false }) {
  const [value, setValue] = useState("");
  const [note, setNote]   = useState("");

  const handleAdd = () => {
    const trimmed = value.trim();
    if (!trimmed || isAdding) return;                 // the Enter key used to send a second add while the first was in flight
    onAdd({ type, value: trimmed, note: note.trim() || undefined }, () => {
      setValue("");
      setNote("");
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
      <div>
        <h2 className="font-semibold text-gray-800">{title}</h2>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>

      {/* Existing entries */}
      <div className="space-y-2">
        {entries.length === 0 && (loadFailed ? (
          // a failed load is not "no rules": the rules exist and are still dropping mail
          <p role="alert" className="text-sm text-red-600">לא ניתן לטעון את הרשימה — החסימות הקיימות אינן מוצגות</p>
        ) : (
          <p className="text-sm text-gray-400 italic">אין חסימות עדיין</p>
        ))}
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100"
          >
            <div className="min-w-0">
              <span className="text-sm font-mono text-gray-800">{entry.value}</span>
              {entry.note && (
                <span className="text-xs text-gray-400 mr-2">— {entry.note}</span>
              )}
              <span className="block text-xs text-gray-400">נוסף ע"י {entry.created_by}</span>
            </div>
            <button
              onClick={() => onRemove(entry.id)}
              disabled={isRemoving}
              className="shrink-0 text-xs text-red-400 hover:text-red-600 border border-red-200 hover:border-red-400 px-2.5 py-1 rounded transition-colors disabled:opacity-50"
            >
              הסר
            </button>
          </div>
        ))}
      </div>

      {/* Add new */}
      <div className="flex gap-2 pt-1">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder={placeholder}
          className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono"
          dir="ltr"
        />
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="הערה (אופציונלי)"
          className="w-40 border rounded-lg px-3 py-2 text-sm"
        />
        <button
          onClick={handleAdd}
          disabled={!value.trim() || isAdding}
          className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shrink-0"
        >
          + הוסף
        </button>
      </div>
    </div>
  );
}

export function CombinedBlocklistSection({ entries, onAdd, onRemove, isAdding, isRemoving = false, error, loadFailed = false }) {
  const [sender, setSender]   = useState("");
  const [keyword, setKeyword] = useState("");
  const [note, setNote]       = useState("");

  const handleAdd = () => {
    if (!sender.trim() || !keyword.trim() || isAdding) return;
    onAdd({ sender: sender.trim(), subject_keyword: keyword.trim(), note: note.trim() || undefined }, () => {
      setSender(""); setKeyword(""); setNote("");
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
      <div>
        <h2 className="font-semibold text-gray-800">חסימה משולבת (AND)</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          מייל ייחסם רק אם <strong>גם</strong> השולח תואם <strong>וגם</strong> הנושא מכיל את מילת המפתח.
        </p>
      </div>

      {/* Existing rules */}
      <div className="space-y-2">
        {entries.length === 0 && (loadFailed ? (
          <p role="alert" className="text-sm text-red-600">לא ניתן לטעון את הרשימה — החוקים הקיימים אינם מוצגים</p>
        ) : (
          <p className="text-sm text-gray-400 italic">אין חוקים משולבים עדיין</p>
        ))}
        {entries.map((rule) => (
          <div
            key={rule.id}
            className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-orange-50 border border-orange-100"
          >
            <div className="min-w-0 flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded">{rule.sender}</span>
              <span className="text-xs text-gray-400">+</span>
              <span className="text-xs font-mono text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded">{rule.subject_keyword}</span>
              {rule.note && <span className="text-xs text-gray-400">— {rule.note}</span>}
            </div>
            <button
              onClick={() => onRemove(rule.id)}
              disabled={isRemoving}
              className="shrink-0 text-xs text-red-400 hover:text-red-600 border border-red-200 hover:border-red-400 px-2.5 py-1 rounded transition-colors disabled:opacity-50"
            >
              הסר
            </button>
          </div>
        ))}
      </div>

      {/* Add new */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 pt-1">
        <input
          type="text"
          value={sender}
          onChange={(e) => setSender(e.target.value)}
          placeholder="כתובת שולח"
          className="border rounded-lg px-3 py-2 text-sm font-mono"
          dir="ltr"
        />
        <input
          type="text"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="מילת מפתח בנושא"
          className="border rounded-lg px-3 py-2 text-sm font-mono"
          dir="ltr"
        />
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="הערה (אופציונלי)"
          className="border rounded-lg px-3 py-2 text-sm"
        />
        <button
          onClick={handleAdd}
          disabled={!sender.trim() || !keyword.trim() || isAdding}
          className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shrink-0"
        >
          + הוסף
        </button>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

const AVATAR_COLORS = [
  "bg-blue-500", "bg-purple-500", "bg-emerald-500",
  "bg-orange-500", "bg-pink-500", "bg-teal-500",
];

function avatarColor(id = "") {
  const hash = id.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function formatLastSeen(dateStr) {
  if (!dateStr) return "לא התחבר מעולם";
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "פעיל כעת";
  if (minutes < 60) return `לפני ${minutes} דקות`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `לפני ${hours} שעות`;
  return `לפני ${Math.floor(hours / 24)} ימים`;
}

export function OnlineTechniciansCard() {
  const { data: technicians = [], isLoading, isError: techniciansFailed } = useQuery({
    queryKey: ["technicians-online"],
    queryFn: () => techniciansApi.online(),
    refetchInterval: 60_000,
  });

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4 sticky top-4">
      <div>
        <h2 className="font-semibold text-gray-800">טכנאים מחוברים כרגע</h2>
        <p className="text-xs text-gray-400 mt-0.5">מתעדכן כל דקה</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
        </div>
      ) : techniciansFailed && technicians.length === 0 ? (
        <p role="alert" className="text-sm text-red-600">לא ניתן לטעון את רשימת הטכנאים המחוברים</p>
      ) : technicians.length === 0 ? (
        <p className="text-sm text-gray-400 italic">אין טכנאים במערכת</p>
      ) : (
        <div className="space-y-3">
          {technicians.map((tech) => {
            const isOnline =
              tech.last_seen &&
              Date.now() - new Date(tech.last_seen).getTime() < 60 * 60 * 1000;
            const initials =
              (tech.first_name?.[0] ?? "") + (tech.last_name?.[0] ?? "");
            return (
              <div key={tech.id} className="flex items-center gap-3">
                <div className="relative shrink-0">
                  <div
                    className={`w-9 h-9 rounded-full ${avatarColor(tech.id)} flex items-center justify-center text-white text-sm font-semibold`}
                  >
                    {initials.toUpperCase()}
                  </div>
                  <span
                    className={`absolute bottom-0 left-0 w-2.5 h-2.5 rounded-full border-2 border-white ${
                      isOnline ? "bg-green-500" : "bg-gray-300"
                    }`}
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {tech.first_name} {tech.last_name}
                  </p>
                  <p className="text-xs text-gray-400">
                    {formatLastSeen(tech.last_seen)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const AI_FEATURES = [
  { key: "ai_summary_enabled",  label: "סיכום אוטומטי",       desc: "סיכום קצר של הבעיה בפתיחת קריאה" },
  { key: "ai_solution_enabled", label: "הצעת פתרון לטכנאי",  desc: 'כפתור "הצע פתרון AI" בדף הקריאה' },
  { key: "ai_category_enabled", label: "סיווג אוטומטי",       desc: "זיהוי קטגוריה: רשת, חומרה וכו'" },
  { key: "ai_technician_pilot_enabled", label: "AI Technician (פיילוט)", desc: "מאפשר ניתוח AI ידני לקריאות — לחץ 'נתח עם AI' בקריאה להפעלה", defaultValue: false },
  { key: "ai_auto_trigger_enabled", label: "טריגר אוטומטי (שלב 2)", desc: "מפעיל ניתוח AI אוטומטי לכל קריאה חדשה — הפעל רק לאחר אימות שלב 1", defaultValue: false },
];

function BackupCard() {
  const [loading, setLoading] = useState(null);
  const [error,   setError]   = useState("");

  const handleDownload = async (format) => {
    setLoading(format);
    setError("");
    try {
      await backupApi.download(format);
    } catch {
      setError("הורדה נכשלה — בדוק שהשרת פעיל ונסה שוב");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
      <div>
        <h2 className="font-semibold text-gray-800">גיבוי נתונים</h2>
        <p className="text-sm text-gray-500 mt-0.5">הורדת כל הקריאות לקובץ מקומי</p>
      </div>

      <div className="flex gap-3 flex-wrap">
        <button
          onClick={() => handleDownload("json")}
          disabled={loading !== null}
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {loading === "json"
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : "📥"
          }
          הורד גיבוי JSON
        </button>

        <button
          onClick={() => handleDownload("csv")}
          disabled={loading !== null}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {loading === "csv"
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : "📥"
          }
          הורד גיבוי CSV
        </button>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <p className="text-xs text-gray-400">
        הקובץ יכלול: מספר קריאה, כותרת, שולח, חברה, טכנאי, סטטוס, עדיפות, קטגוריית AI, תיאור ותאריכים.
        קובץ CSV נפתח ישירות ב-Excel עם תמיכה בעברית.
      </p>
    </div>
  );
}

export function AiSettingsCard() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saved, setSaved] = useState(false);

  const { data: config = {}, isLoading, isError: configFailed } = useQuery({
    queryKey: ["ai-settings"],
    queryFn: () => aiApi.getSettings(),
  });

  const { data: connHealth } = useQuery({
    queryKey: ["connections-health"],
    queryFn: () => connectionsApi.health(),
    staleTime: 60000,
  });
  const aiStatus = connHealth?.ai_analysis;

  const [flags, setFlags] = useState({});
  const effectiveFlags = { ...config, ...flags };

  const saveMutation = useMutation({
    mutationFn: (data) => aiApi.saveSettings(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-settings"] });
      setApiKey("");
      setFlags({});
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשמירת ההגדרות", "error"),
  });

  const handleSave = () => {
    const payload = { ...flags };
    if (apiKey.trim()) payload.openai_api_key = apiKey.trim();
    saveMutation.mutate(payload);
  };

  const hasChanges = apiKey.trim() || Object.keys(flags).length > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6 space-y-5">
      <div>
        <h2 className="font-semibold text-gray-800">✨ הגדרות AI (OpenAI)</h2>
        <p className="text-sm text-gray-500 mt-0.5">סיכום אוטומטי, סיווג וניתוח קריאות עם GPT-4o-mini</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
        </div>
      ) : (
        <>
          {/* API Key */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">OpenAI API Key</label>
            <div className="flex gap-2 items-center">
              <div className="flex-1 relative">
                <input
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={config.api_key_set ? "••••••••••••••• (מוגדר — השאר ריק לשמור)" : "sk-..."}
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  dir="ltr"
                />
              </div>
              <button
                onClick={() => setShowKey(v => !v)}
                className="text-xs text-gray-400 hover:text-gray-600 border rounded px-2.5 py-2"
              >
                {showKey ? "הסתר" : "הצג"}
              </button>
            </div>
            {configFailed ? (
              // A failed read of the current settings must not read as "no key configured": config
              // falls back to {} either way, and api_key_set being falsy would otherwise claim the
              // AI feature is definitely off when the truth is simply unknown right now.
              <p role="alert" className="text-xs text-red-600">לא ניתן לטעון את מצב הגדרות ה-AI הנוכחי</p>
            ) : (
              <p className="text-xs text-gray-400">
                {config.api_key_set ? "✓ מפתח API מוגדר" : "מפתח API לא מוגדר — AI לא יפעל"}
              </p>
            )}
          </div>

          {/* Feature toggles */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">תכונות</p>
            {AI_FEATURES.map(({ key, label, desc, defaultValue = true }) => {
              const enabled = flags[key] !== undefined ? flags[key] : (config[key] !== undefined ? config[key] : defaultValue);
              return (
                <label key={key} className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => setFlags(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="sr-only"
                    />
                    {/* No onClick here: the enclosing <label> already forwards a click on the pill to the checkbox
                        above. A handler on the pill as well toggled twice (pill click = no change at all). */}
                    <div
                      className={`w-9 h-5 rounded-full transition-colors cursor-pointer ${enabled ? "bg-violet-600" : "bg-gray-300"}`}
                    >
                      <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{label}</p>
                    <p className="text-xs text-gray-400">{desc}</p>
                  </div>
                </label>
              );
            })}
          </div>

          {effectiveFlags.ai_technician_pilot_enabled && (
            <div className="rounded-lg bg-violet-50 border border-violet-200 px-4 py-3 text-xs text-violet-800 space-y-2">
              <div>
                <p className="font-medium mb-1">שלב 1 — הפעלת כפתור AI ידני:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>עבור ל<Link to="/agents" className="underline font-medium">דף הסוכנים</Link> וצור סוכן חדש בשם <strong>AI Technician Pilot</strong></li>
                  <li>בחר מודל <strong>claude-haiku-4-5-20251001</strong> ואפשר את הסוכן</li>
                </ol>
              </div>
              {effectiveFlags.ai_auto_trigger_enabled && (
                <div className="border-t border-violet-200 pt-2">
                  <p className="font-medium text-orange-700 mb-0.5">⚠ שלב 2 — טריגר אוטומטי פעיל</p>
                  <p className="text-orange-600">כל קריאה חדשה תתחיל ניתוח AI אוטומטי. ודא ששלב 1 אומת לפני הפעלה.</p>
                </div>
              )}
            </div>
          )}

          {aiStatus && (
            <div className={`rounded-lg p-3 text-xs border ${
              aiStatus.configured
                ? "bg-green-50 border-green-200 text-green-700"
                : "bg-amber-50 border-amber-200 text-amber-700"
            }`}>
              {aiStatus.configured
                ? `✓ DirectLLM AI מוגדר — ספק: ${aiStatus.provider || "?"} · מודל: ${aiStatus.model || "?"}`
                : "⚠ DirectLLM AI אינו מוגדר — הגדר ANALYSIS_PROVIDER, ANALYSIS_MODEL ומפתח API ב-.env לפעולת AI Technician"}
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={handleSave}
              disabled={!hasChanges || saveMutation.isPending}
              className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {saveMutation.isPending ? "שומר..." : "שמור הגדרות"}
            </button>
            {saved && <span className="text-sm text-green-600 font-medium">✓ נשמר בהצלחה</span>}
          </div>
        </>
      )}
    </div>
  );
}

export function ApiTokensCard() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [name, setName] = useState("");
  const [newToken, setNewToken] = useState(null);
  const [copied, setCopied] = useState(false);

  const { data: tokens = [], isLoading, isError: tokensFailed } = useQuery({
    queryKey: ["api-tokens"],
    queryFn: () => apiTokensApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => apiTokensApi.create(data),
    onSuccess: (created) => {
      setNewToken(created);
      setName("");
      qc.invalidateQueries({ queryKey: ["api-tokens"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת ה-API Token", "error"),
  });

  const revokeMutation = useMutation({
    mutationFn: (id) => apiTokensApi.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-tokens"] }),
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בביטול ה-API Token", "error"),
  });

  const handleCopy = () => {
    navigator.clipboard.writeText(newToken.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
      <div>
        <h2 className="font-semibold text-gray-800">API Tokens</h2>
        <p className="text-sm text-gray-500 mt-0.5">חיבור מערכות חיצוניות באמצעות Bearer token</p>
      </div>

      {newToken && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
          <p className="text-sm font-semibold text-amber-800">⚠ שמור את ה-Token — לא יוצג שוב</p>
          <div className="flex items-center gap-2">
            <code
              className="flex-1 bg-white border border-amber-200 rounded px-3 py-2 text-xs font-mono text-gray-800 break-all select-all"
              dir="ltr"
            >
              {newToken.token}
            </code>
            <button
              onClick={handleCopy}
              className="shrink-0 text-xs px-3 py-2 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 transition-colors font-medium"
            >
              {copied ? "הועתק ✓" : "העתק"}
            </button>
          </div>
          <button
            onClick={() => { setNewToken(null); setCopied(false); }}
            className="text-xs text-amber-600 hover:text-amber-800 underline"
          >
            סגור
          </button>
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          // two Enters (or key repeat) before the answer created two long-lived bearer tokens, and only the last raw value is shown
          onKeyDown={(e) => e.key === "Enter" && name.trim() && !createMutation.isPending && createMutation.mutate({ name: name.trim() })}
          placeholder="שם ה-Token (למשל: Zapier, N8N, Make)"
          className="flex-1 border rounded-lg px-3 py-2 text-sm"
        />
        <button
          onClick={() => createMutation.mutate({ name: name.trim() })}
          disabled={!name.trim() || createMutation.isPending}
          className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shrink-0"
        >
          + צור Token
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
        </div>
      ) : tokensFailed && tokens.length === 0 ? (
        <p role="alert" className="text-sm text-red-600">לא ניתן לטעון את רשימת ה-API tokens</p>
      ) : tokens.length === 0 ? (
        <p className="text-sm text-gray-400 italic">אין API tokens פעילים</p>
      ) : (
        <div className="space-y-2">
          {tokens.map((token) => (
            <div
              key={token.id}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800">{token.name}</p>
                <p className="text-xs text-gray-400">
                  נוצר {new Date(token.created_at).toLocaleDateString("he-IL")} ע"י {token.created_by}
                </p>
                {token.last_used_at ? (
                  <p className="text-xs text-gray-400">
                    שימוש אחרון: {new Date(token.last_used_at).toLocaleDateString("he-IL")}
                  </p>
                ) : (
                  <p className="text-xs text-gray-300 italic">טרם נוצל</p>
                )}
              </div>
              <button
                onClick={() => revokeMutation.mutate(token.id)}
                disabled={revokeMutation.isPending}
                className="shrink-0 text-xs text-red-400 hover:text-red-600 border border-red-200 hover:border-red-400 px-2.5 py-1 rounded transition-colors"
              >
                בטל
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBar({ percent, redThreshold = 80, orangeThreshold = 60 }) {
  const color =
    percent >= redThreshold    ? "bg-red-500"    :
    percent >= orangeThreshold ? "bg-orange-400" :
    "bg-green-500";
  return (
    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
      <div className={`h-1.5 rounded-full transition-all ${color}`} style={{ width: `${Math.min(percent, 100)}%` }} />
    </div>
  );
}

function StatusBadge({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
      ok ? "bg-green-50 text-green-700 border border-green-200" : "bg-orange-50 text-orange-700 border border-orange-200"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-green-500" : "bg-orange-400"}`} />
      {label}
    </span>
  );
}

function ServerStatusCard() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [refreshing, setRefreshing] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["server-status"],
    queryFn: () => serverApi.status(),
    refetchOnWindowFocus: false,
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await serverApi.refresh();
      await qc.invalidateQueries({ queryKey: ["server-status"] });
    } catch (err) {
      addNotification(err?.response?.data?.detail || "שגיאה ברענון נתוני השרת", "error");
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-800">🖥 נתוני שרת</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {data?.last_updated ? `עודכן: ${formatDate(data.last_updated)}${data.cached ? " (מהמטמון)" : ""}` : ""}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || isLoading}
          className="flex items-center gap-1.5 text-xs text-brand-600 hover:text-brand-800 border border-brand-200 hover:border-brand-400 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
        >
          {refreshing ? (
            <span className="w-3.5 h-3.5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
          ) : "🔄"}
          רענן עכשיו
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-500">שגיאה בטעינת נתוני השרת</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Disk */}
          <div className="space-y-2 p-4 rounded-lg bg-gray-50 border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">💾 דיסק</span>
              {data?.disk ? (
                <StatusBadge ok={data.disk.percent < 80} label={`${data.disk.percent}%`} />
              ) : <span className="text-xs text-gray-400">—</span>}
            </div>
            {data?.disk ? (
              <>
                <p className="text-xs text-gray-500">
                  {data.disk.free_gb} GB פנוי מתוך {data.disk.total_gb} GB
                </p>
                <StatusBar percent={data.disk.percent} redThreshold={80} orangeThreshold={60} />
              </>
            ) : <p className="text-xs text-gray-400 italic">לא זמין</p>}
          </div>

          {/* Uptime */}
          <div className="space-y-2 p-4 rounded-lg bg-gray-50 border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">⏱ זמן פעולה</span>
              {data?.uptime ? (
                <StatusBadge ok label="פעיל" />
              ) : <span className="text-xs text-gray-400">—</span>}
            </div>
            {data?.uptime ? (
              <p className="text-xs text-gray-500">
                {data.uptime.days} ימים, {data.uptime.hours} שעות, {data.uptime.minutes} דקות
              </p>
            ) : <p className="text-xs text-gray-400 italic">לא זמין</p>}
          </div>

          {/* RAM */}
          <div className="space-y-2 p-4 rounded-lg bg-gray-50 border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">🧠 RAM</span>
              {data?.ram ? (
                <StatusBadge ok={data.ram.percent < 80} label={`${data.ram.percent}%`} />
              ) : <span className="text-xs text-gray-400">—</span>}
            </div>
            {data?.ram ? (
              <>
                <p className="text-xs text-gray-500">
                  {data.ram.used_mb.toLocaleString()} MB בשימוש מתוך {data.ram.total_mb.toLocaleString()} MB
                </p>
                <StatusBar percent={data.ram.percent} redThreshold={90} orangeThreshold={80} />
              </>
            ) : <p className="text-xs text-gray-400 italic">לא זמין</p>}
          </div>

          {/* Updates */}
          <div className="space-y-2 p-4 rounded-lg bg-gray-50 border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">🔄 עדכונים זמינים</span>
              {data?.updates != null ? (
                <StatusBadge ok={data.updates.count === 0} label={data.updates.count === 0 ? "מעודכן" : `${data.updates.count} עדכונים`} />
              ) : <span className="text-xs text-gray-400">—</span>}
            </div>
            {data?.updates != null ? (
              <p className="text-xs text-gray-500">
                {data.updates.count === 0 ? "השרת מעודכן לגמרי" : `${data.updates.count} חבילות ממתינות לעדכון`}
              </p>
            ) : <p className="text-xs text-gray-400 italic">לא זמין</p>}
          </div>

          {/* Backups — full width */}
          <div className="sm:col-span-2 space-y-2 p-4 rounded-lg bg-gray-50 border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">📦 גיבויים (/opt/backups)</span>
              {data?.backups ? (
                <StatusBadge ok={data.backups.count > 0} label={data.backups.count > 0 ? "קיימים גיבויים" : "אין גיבויים"} />
              ) : <span className="text-xs text-gray-400">—</span>}
            </div>
            {data?.backups ? (
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
                <span>{data.backups.count} קבצים</span>
                <span>גודל כולל: {data.backups.total_size_gb >= 1 ? `${data.backups.total_size_gb} GB` : `${data.backups.total_size_mb} MB`}</span>
                <span>גיבוי אחרון: {formatDate(data.backups.last_backup)}</span>
              </div>
            ) : <p className="text-xs text-gray-400 italic">לא זמין — תיקיית /opt/backups לא נמצאה</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const qc = useQueryClient();
  const [error, setError] = useState("");

  const { data: blocklist = [], isLoading, isError: blocklistFailed } = useQuery({
    queryKey: ["blocklist"],
    queryFn:  () => settingsApi.blocklist.list(),
  });

  const { data: combinedRules = [], isError: combinedFailed } = useQuery({
    queryKey: ["blocklist-combined"],
    queryFn:  () => settingsApi.combinedBlocklist.list(),
  });

  const addMutation = useMutation({
    mutationFn: (data) => settingsApi.blocklist.add(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["blocklist"] }); setError(""); },
    onError:   (err) => setError(err.response?.data?.detail || "שגיאה בהוספת ערך"),
  });

  const removeMutation = useMutation({
    mutationFn: (id) => settingsApi.blocklist.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["blocklist"] }); setError(""); },
    onError: (err) => setError(err?.response?.data?.detail || "שגיאה בהסרת הערך"),
  });

  const addCombinedMutation = useMutation({
    mutationFn: (data) => settingsApi.combinedBlocklist.add(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["blocklist-combined"] }); setCombinedError(""); },
    onError:   (err) => setCombinedError(err.response?.data?.detail || "שגיאה בהוספת כלל"),
  });

  const removeCombinedMutation = useMutation({
    mutationFn: (id) => settingsApi.combinedBlocklist.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["blocklist-combined"] }); setCombinedError(""); },
    onError: (err) => setCombinedError(err?.response?.data?.detail || "שגיאה בהסרת הכלל"),
  });

  const [combinedError, setCombinedError] = useState("");

  const handleAdd = (data, onDone) => {
    addMutation.mutate(data, { onSuccess: onDone });
  };

  const senderEntries  = blocklist.filter((e) => e.type === "sender");
  const keywordEntries = blocklist.filter((e) => e.type === "subject_keyword");

  return (
    <div className="max-w-5xl" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">הגדרות מערכת</h1>
        <p className="text-sm text-gray-500 mt-1">הגדרות אדמין — רשימות חסימה לקליטת מיילים</p>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-6 items-start">
        {/* ימין — רשימות חסימה */}
        <div className="space-y-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
            </div>
          ) : (
            <>
              <ServerStatusCard />

              <BlocklistSection
                title="חסימת שולחים"
                description="מיילים שמגיעים מכתובות אלו לא ייצרו קריאה. השוואה מלאה לכתובת המייל."
                type="sender"
                placeholder="noreply@example.com"
                entries={senderEntries}
                loadFailed={blocklistFailed}
                onAdd={handleAdd}
                onRemove={(id) => removeMutation.mutate(id)}
                isAdding={addMutation.isPending}
                isRemoving={removeMutation.isPending}
              />

              <BlocklistSection
                title="חסימת מילות מפתח בנושא"
                description="מיילים שנושאם מכיל את המילה/ביטוי ייחסמו. ללא תלות בגודל האות."
                type="subject_keyword"
                placeholder="BACKUP SUCCEEDED"
                entries={keywordEntries}
                loadFailed={blocklistFailed}
                onAdd={handleAdd}
                onRemove={(id) => removeMutation.mutate(id)}
                isAdding={addMutation.isPending}
                isRemoving={removeMutation.isPending}
              />

              <CombinedBlocklistSection
                entries={combinedRules}
                loadFailed={combinedFailed}
                onAdd={(data, onDone) => addCombinedMutation.mutate(data, { onSuccess: onDone })}
                onRemove={(id) => removeCombinedMutation.mutate(id)}
                isAdding={addCombinedMutation.isPending}
                isRemoving={removeCombinedMutation.isPending}
                error={combinedError}
              />

              <BackupCard />

              <AiSettingsCard />

              <ApiTokensCard />
            </>
          )}
        </div>

        {/* שמאל — טכנאים מחוברים */}
        <OnlineTechniciansCard />
      </div>
    </div>
  );
}
